import { graphqlRequest } from '../urql';
import { get } from 'lodash';
import { isEmpty, isOk } from '@/common/utils';
import {
  getAssetByIdQuery,
  getInProjectAssetsQuery,
  getMediaQuery,
  getAlbumCategoryQuery,
  getUserAlbumsQuery,
  getCommunityAlbumsQuery,
  getQrrentByIdQuery,
  getAlbumByIdQuery,
  getUserAvailableAlbumsQuery,
  getDigitalFrameInProjectAssetsQuery,
  getPageInProjectAssetsQuery
} from './queries';
import {
  containerMapping,
  extractAlbumCategories,
  mediaMapping,
  parseAPIAlbums
} from '@/common/mapping';

import {
  PictureAssetEntity,
  VideoAssetEntity
} from '@/common/models/entities/asset';
import axios from 'axios';
import responseHandler from '../urql/responseHandler';

export const getPhotosApi = async (id, projectId, terms = []) => {
  if (isEmpty(terms)) return [];

  const res = await graphqlRequest(getMediaQuery, { id, terms, projectId });

  if (!isOk(res)) return [];
  const searchAssets = get(res, 'data.search_community_assets', []) || [];
  return searchAssets
    ?.filter(asset => !asset.is_media)
    .map(asset => new PictureAssetEntity(mediaMapping(asset)));
};

export const getMediaApi = async (id, projectId, terms = []) => {
  if (isEmpty(terms)) return [];

  const res = await graphqlRequest(getMediaQuery, { id, terms, projectId });

  if (!isOk(res)) return [];
  const searchAssets = get(res, 'data.search_community_assets', []) || [];
  return searchAssets.map(asset => {
    return asset.is_media
      ? new VideoAssetEntity(mediaMapping(asset, !asset.is_media))
      : new PictureAssetEntity(mediaMapping(asset));
  });
};

/**
 * To fetch data of a asset by its id
 *
 * @param {String} assetId asset id
 * @returns {Object} asset data
 */
export const getAssetByIdApi = async (assetId, projectId) => {
  const res = await graphqlRequest(getAssetByIdQuery, {
    id: assetId,
    projectId
  });

  if (!isOk(res)) return;

  const asset = res.data.asset;
  return asset.is_media
    ? new VideoAssetEntity(mediaMapping(asset, !asset.is_media))
    : new PictureAssetEntity(mediaMapping(asset));
};

const getAlbumCategoryWithPage = async ({ communityId, page }) => {
  const res = await graphqlRequest(getAlbumCategoryQuery, {
    communityId,
    page
  });

  if (!isOk(res)) return {};

  return res;
};

export const getAlbumCategoryApi = async communityId => {
  let responseTotal = {
    community_containers: [],
    community_group_assets: [],
    user_containers: []
  };
  let isContinue = true;
  let i = 1;
  const perPages = 100;
  while (isContinue) {
    try {
      const res = await getAlbumCategoryWithPage({
        communityId,
        page: i
      });
      i++;
      const user_containers = get(res, 'data.user_containers', []);
      const community_containers = get(res, 'data.community_containers', []);
      const group_assets = get(res, 'data.community_group_assets', []);

      responseTotal.user_containers.push(...user_containers);
      responseTotal.community_containers.push(...community_containers);
      responseTotal.community_group_assets = group_assets;
      if (
        community_containers.length < perPages &&
        user_containers.length < perPages
      ) {
        isContinue = false;
      }
    } catch (ex) {
      isContinue = false;
    }
  }

  const communities = get(responseTotal, 'community_containers', []);
  const groups = get(responseTotal, 'community_group_assets', []);
  const personalAlbums = get(responseTotal, 'user_containers', []);
  const personalCate = extractAlbumCategories(personalAlbums);
  // mapping list of categories
  return {
    communities: extractAlbumCategories(communities),
    groups: extractAlbumCategories(groups, true),
    personalAlbums: personalCate || []
  };
};

export const getUserAlbumsApi = async page => {
  const res = await graphqlRequest(getUserAlbumsQuery, {
    page
  });

  if (!isOk(res)) return;

  const personalAlbums = get(res, 'data.user_containers', []);
  return parseAPIAlbums(personalAlbums);
};

export const getCommunityAlbumsApi = async (communityId, projectId, page) => {
  const res = await graphqlRequest(getCommunityAlbumsQuery, {
    communityId,
    projectId,
    page
  });

  if (!isOk(res)) return;

  const communities = get(res, 'data.community_containers', []);

  return parseAPIAlbums(communities);
};

export const getAlbumByIdApi = async (id, projectId) => {
  const res = await graphqlRequest(getAlbumByIdQuery, {
    id,
    projectId
  });

  if (!isOk(res)) return;

  const album = get(res, 'data.container', []);
  // normalize albums
  return containerMapping(album);
};

/**
 *  Qrrent is a group of albums
 */
export const getQrrentByIdApi = async (id, projectId) => {
  const res = await graphqlRequest(getQrrentByIdQuery, {
    id,
    projectId
  });

  if (!isOk(res)) return;

  const container = get(res, 'data.qrrent', []);
  // normalize albums
  return parseAPIAlbums([container]).reverse();
};

/**
 * To get in project asset of book and current page / frame
 * @param {String} bookId id of current book
 * @param {String | Array} projectId id of current project (pages / frame)
 * @param {Boolean} isDigital is digital edition or not
 * @returns assets id of current project and of whole book
 */
export const getInProjectAssetsApi = async (bookId, projectId, isDigital) => {
  const book = await graphqlRequest(getInProjectAssetsQuery, { bookId }, true);
  let leftPageAssetIds = [];
  let rightPageAssetIds = [];
  let apiPageAssetIds = [];

  if (isDigital) {
    const res = await graphqlRequest(
      getDigitalFrameInProjectAssetsQuery,
      {
        frameId: projectId
      },
      true
    );
    if (!isOk(res)) return {};
    apiPageAssetIds = get(res, 'data.digital_frame.assets', []).map(
      asset => asset.id
    );
  } else {
    const projectIdsArray = Array.isArray(projectId) ? projectId : [projectId];
    const promises = projectIdsArray.map(id =>
      graphqlRequest(
        getPageInProjectAssetsQuery,
        {
          pageId: id
        },
        true
      )
    );
    const res = await Promise.all(promises);
    if (!isOk(res)) return {};
    leftPageAssetIds = get(res[0], 'data.page.assets', []).map(
      asset => asset.id
    );
    rightPageAssetIds = get(res[1], 'data.page.assets', []).map(
      asset => asset.id
    );

    apiPageAssetIds = [...leftPageAssetIds, ...rightPageAssetIds];
  }

  const apiBookAssetIds = get(book, 'data.book.in_project_assets', []).map(
    asset => asset.id
  );
  return {
    bookId,
    apiBookAssetIds,
    apiPageAssetIds,
    leftPageAssetIds,
    rightPageAssetIds
  };
};

const getUserAvailableAlbumApiWithPage = async ({ page, perPages }) => {
  const res = await graphqlRequest(getUserAvailableAlbumsQuery, {
    page,
    perPages
  });

  if (!isOk(res)) return {};

  return res;
};

export const getUserAvailableAlbumApi = async () => {
  const perPages = 100;
  let page = 1;
  const totalResponse = {
    user_available_containers: []
  };
  let isContinue = true;
  while (isContinue) {
    try {
      const res = await getUserAvailableAlbumApiWithPage({ page, perPages });
      const albums = get(res, 'data.user_available_containers', []);
      totalResponse.user_available_containers.push(...albums);
      page++;
      if (albums && albums.length < perPages) {
        isContinue = false;
      }
    } catch (ex) {
      isContinue = false;
    }
  }

  return parseAPIAlbums(totalResponse.user_available_containers);
};

/**
 * To upload media file to Platypus
 *
 * @param {Object} asset File data
 * @param {Object} uploadToken upload token
 * @returns upload asset object
 */
export const uploadAssetsApi = async (asset, uploadToken) => {
  try {
    const { url, token } = uploadToken;

    const formData = new FormData();
    formData.append('upload', asset);
    formData.append('authenticationToken', token);

    return axios.post(url, formData);
  } catch (error) {
    error.message = ` An error occurred while uploading ${asset.name}`;
    return responseHandler({ error });
  }
};
